import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Briefcase, ExternalLink } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/uiDir/avatar";
import { Button } from "@/components/uiDir/button";

interface Opportunity {
  project_id: string;
  opportunity: string;
  author_username: string;
  author_avatar: string;
  author_name: string;
  project_title: string;
}

const getInitials = (name: string) => {
    if (!name) return '??';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const AllOpportunities = () => {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        const { data, error } = await supabase.rpc('get_all_opportunities');

        if (error) {
          throw error;
        }

        setOpportunities(data || []);
      } catch (error) {
        console.error("Error fetching opportunities:", error);
        toast.error("Failed to load opportunities.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOpportunities();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Loading Opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-4xl">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-heading font-bold mb-2">Collaboration Opportunities</h1>
          <p className="text-muted-foreground">Find projects to contribute to and grow your skills.</p>
        </div>

        {opportunities.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {opportunities.map((op) => (
              <Card key={op.project_id + op.opportunity} className="glass-card hover:shadow-lg transition-shadow">
                <CardHeader>
                    <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                            <AvatarImage src={op.author_avatar} />
                            <AvatarFallback>{getInitials(op.author_name)}</AvatarFallback>
                        </Avatar>
                        <div>
                            <p className="font-semibold text-sm">{op.author_name}</p>
                            <p className="text-xs text-muted-foreground">@{op.author_username}</p>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <p className="text-sm text-muted-foreground">Is looking for a</p>
                    <p className="text-lg font-bold text-primary">{op.opportunity}</p>
                    <p className="text-sm text-muted-foreground">for the project "{op.project_title}"</p>
                  </div>
                  <Link to={`/profile/${op.author_username}#${op.project_id}`}>
                    <Button className="w-full">
                      View Project & Apply
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="glass-card text-center py-12">
            <CardContent>
              <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-lg font-semibold">No Opportunities Available</h2>
              <p className="text-sm text-muted-foreground">Check back later for new collaboration roles.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AllOpportunities;